import React, { useEffect, useState, useRef, useContext } from "react";
import { Alert } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import MusicLinkUrlpartTable from "../../components/musicLinkUrlpartTable";
import Pagination from "../../components/pagination/pagination";
import TableModal from "../../components/tableModal";
import ViewLayout from "../../components/viewLayout";
import { useFetch } from "../../utils/fetchHook";

import { StateContext } from "../../utils/context";

import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";

import {
  deleteMusicLinkUrlPart,
  setMusicLinks,
} from "../../redux/musicLinks/action";
import {
  getDeletedDemoMusicLinkUrlPartIds,
  getDemoMusicLinkUrlPartRows,
  removeDemoMusicLinkUrlPartRow,
  upsertDemoMusicLinkUrlPartRow,
} from "../../utils/demoMusicLinkUrlPartsSessionStore";
import {
  enrichMusicLinkUrlPartRow,
  filterMusicLinkUrlPartRows,
} from "../../utils/musicLinkUrlPartsDemoHelpers";
import { isBrowserSessionDemo, isDemoMode } from "../../utils/demoConfig";

const mergeById = (baseRows, extraRows) => {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
};

const MusicLinkUrlpart = () => {
  const dispatch = useDispatch();
  const reduxUrlPartExtras = useSelector(state => state.musicLinks?.created || []);

  const [openModal, setToggleModal] = useState(false);
  const [dataModal, setDataModal] = useState(null);
  const [deleteBody, setDeleteBody] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorPut, setError] = useState(false);

  const [forceUpdate, setForce] = useState(1);

  const handlerValues = { setSuccess, setError, forceUpdate, setForce };
  const [tableData, setTableData] = useState([]);

  const didEff1 = useRef(false);
  const didEff2 = useRef(false);
  const [dataCount, setDataCount] = useState(0);
  const [currrentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [subUser, setSubUser] = useState("");
  const [subUserEndpoint, setSubUserEndpoint] = useState("");
  const [parentUserOnly, setParentUserOnly] = useState("");
  const [ordering, setOrdering] = useState({
    order: "",
    name: "part",
    sort: "part",
  });

  const token = getCookie("token");
  const { currentUser } = useContext(StateContext);

  const shouldUseDemoDelete = isDemoMode() || isBrowserSessionDemo();
  const shouldUseLocalPagination = shouldUseDemoDelete;
  const fetchPage = shouldUseLocalPagination ? 1 : currrentPage;
  const fetchPageSize = shouldUseLocalPagination ? 1000 : pageSize;

  const [urlparts, errorUrlparts, loadingUrlparts] = useFetch(
    "GET",
    `musiclinks_urlparts/${subUserEndpoint}?ordering=${ordering.order + ordering.name}&page=${fetchPage}&page_size=${fetchPageSize}&search=${query}${parentUserOnly}`,
    token,
    false,
    forceUpdate,
  );

  const defaultSorted = [
    {
      dataField: ordering.sort,
    },
  ];

  const tableProps = {
    tableData,
    defaultSorted,
  };

  const handleDemoDelete = () => {
    if (!dataModal?.id) {
      return;
    }
    removeDemoMusicLinkUrlPartRow(dataModal.id);
    dispatch(deleteMusicLinkUrlPart(dataModal.id));
    setTableData(prev => prev.filter(row => String(row?.id) !== String(dataModal.id)));
    setDataCount(prev => Math.max(prev - 1, 0));
    setToggleModal(false);
    setDeleteBody(false);
    setDataModal(null);
    setError(false);
    setSuccess(true);
  };

  useEffect(() => {
    if (urlparts?.detail === "Invalid page.") {
      setCurrentPage(1);
      return;
    }

    if (!urlparts) {
      return;
    }

    const apiRows = Array.isArray(urlparts.results) ? urlparts.results : [];
    const deletedIds = new Set(getDeletedDemoMusicLinkUrlPartIds().map(String));
    const visibleApiRows = apiRows.filter(row => !deletedIds.has(String(row?.id)));
    if (Array.isArray(urlparts.results)) {
      dispatch(setMusicLinks(urlparts.results));
    }

    const sessionRows = getDemoMusicLinkUrlPartRows();
    const mergedRows = mergeById(mergeById(visibleApiRows, sessionRows), reduxUrlPartExtras)
      .filter(row => !deletedIds.has(String(row?.id)))
      .map(enrichMusicLinkUrlPartRow)
      .sort((a, b) => String(a?.part || "").localeCompare(String(b?.part || "")));

    mergedRows.forEach(row => upsertDemoMusicLinkUrlPartRow(row));

    const filteredRows = filterMusicLinkUrlPartRows(mergedRows, query);

    if (shouldUseLocalPagination) {
      const totalRows = filteredRows.length;
      const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
      const boundedPage = Math.min(currrentPage, totalPages);
      if (boundedPage !== currrentPage) {
        setCurrentPage(boundedPage);
        return;
      }
      const start = (boundedPage - 1) * pageSize;
      const end = start + pageSize;
      setTableData(filteredRows.slice(start, end));
      setDataCount(totalRows);
      return;
    }

    setTableData(filteredRows);
    setDataCount(Number(urlparts?.count) || filteredRows.length);
  }, [
    urlparts,
    currrentPage,
    dispatch,
    pageSize,
    query,
    reduxUrlPartExtras,
    shouldUseLocalPagination,
  ]);

  useEffect(() => {
    if (!didEff1.current) {
      didEff1.current = true;
      return;
    }
    if (shouldUseLocalPagination) {
      return;
    }
    setForce(prev => prev + 1);
  }, [currrentPage, ordering.order, ordering.name, pageSize, shouldUseLocalPagination, subUser]);

  useEffect(() => {
    if (!didEff2.current) {
      didEff2.current = true;
      return;
    }
    const timeout = setTimeout(() => {
      setForce(prev => prev + 1);
    }, 500);
    return () => clearTimeout(timeout);
  }, [query]);

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currrentPage,
  };

  return (
    <>
      {!currentUser.music_link && !loadingUrlparts ? (
        <Alert color="danger">
          Currently this feature is only offered to few selected users but the good news is that you will also be able to use it really soon. Keep watching this space!
        </Alert>
      ) : currentUser.music_link && loadingUrlparts ? (
        <TableHelper loading />
      ) : errorUrlparts ? (
        <TableHelper error />
      ) : (
        <div>
          <ViewLayout title={"Your music links urlpart list"}>
            <MusicLinkUrlpartTable
              {...tableProps}
              setToggleModal={setToggleModal}
              setDataModal={setDataModal}
              setDeleteBody={setDeleteBody}
              forceUpdate={forceUpdate}
              searchQuery={{ setQuery, query, ordering, setOrdering }}
              subUserData={{
                subUser,
                setSubUser,
                subUserEndpoint,
                setSubUserEndpoint,
                setParentUserOnly,
              }}
            />
          </ViewLayout>

          <Pagination pagination={paginationProps} />
        </div>
      )}

      {dataModal && (
        <TableModal
          openModal={openModal}
          setToggleModal={setToggleModal}
          dataModal={dataModal}
          values={handlerValues}
          title={
            deleteBody
              ? dataModal.can_be_deleted
                ? `Delete ${dataModal.part}?`
                : `Can't Delete music-link-urlpart ${dataModal.part}`
              : `Edit ${dataModal.part}?`
          }
          body={
            !dataModal.can_be_deleted
              ? `Music-link-urlpart ${dataModal.part} can't be deleted as it is already associated with a Music Link.`
              : "Are you really sure you really want to delete this music-link-urlpart?"
          }
          apiURL={`musiclinks_urlparts/${dataModal.id}/`}
          bodyID={dataModal.id}
          deleteBody={deleteBody}
          can_be_deleted={dataModal.can_be_deleted}
          onDeleteConfirm={shouldUseDemoDelete ? handleDemoDelete : undefined}
        />
      )}
      {success && <Alert color="success">Music Link Urlpart has been removed!</Alert>}
      {errorPut && (
        <Alert color="danger">
          Something went wrong! Please refresh page and try again!
        </Alert>
      )}
    </>
  );
};

export default MusicLinkUrlpart;
